import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { FadeUp, MaskLines } from "../components/Reveal";

const METHOD = [
  { num: "01", title: "Inquiry", body: "A conversation about how you live or work. We listen first — the brief, the budget, the horizon." },
  { num: "02", title: "Spatial Diagnosis", body: "We survey the site, trace the sun, and map what the space already wants to be." },
  { num: "03", title: "Concept & Schematics", body: "Massing, mood and material direction. You see the idea before a single wall is drawn." },
  { num: "04", title: "Material Realization", body: "Drawings become joinery, plaster and stone — with samples in your hand, not on a screen." },
  { num: "05", title: "Site Supervision & Handover", body: "We stay until the last detail is set, and hand over a home with its own manual of care." },
];

export default function Services() {
  const [services, setServices] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  return (
    <main className="pt-28 sm:pt-40 px-6 sm:px-10 pb-24 sm:pb-32">
      <div className="max-w-[1400px] mx-auto">
        <FadeUp>
          <p className="flex items-center gap-3 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#8A857D]">
            <span className="h-px w-10 bg-[#C29B38]" /> Disciplines
          </p>
        </FadeUp>
        <MaskLines
          as="h1"
          delay={0.1}
          className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight"
          lines={["Four disciplines,", { node: <span><em className="italic">one stillness</em>.</span> }]}
        />
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-xl font-light text-[#57534E] text-lg leading-relaxed">
            Every commission moves through the same unhurried rhythm — whether it is a villa, a gallery or a single room that needs to breathe again.
          </p>
        </FadeUp>

        <div className="mt-20">
          {(services || []).map((s, i) => (
            <FadeUp key={s.id}>
              <div className="grid md:grid-cols-12 gap-4 md:gap-8 py-10 sm:py-14 border-t hairline" data-testid={`service-item-${i}`}>
                <div className="md:col-span-2 font-serif italic text-5xl sm:text-6xl text-[#C29B38]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="md:col-span-4 font-serif text-2xl sm:text-4xl leading-tight">{s.title}</h3>
                <p className="md:col-span-6 font-light text-[#57534E] leading-relaxed">{s.description}</p>
              </div>
            </FadeUp>
          ))}
          {services !== null && services.length === 0 && (
            <p className="font-light text-[#8A857D]">Services will appear here once added in the Studio Dashboard.</p>
          )}
        </div>

        <FadeUp>
          <div className="mt-24 bg-[#EFECE6] px-6 sm:px-14 py-14 sm:py-20">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#8A857D]">The Studio Method</p>
            <h2 className="mt-4 font-serif text-3xl sm:text-5xl tracking-tight">Five stages. <em className="italic">Zero surprises.</em></h2>
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
              {METHOD.map((m) => (
                <div key={m.num} className="border-t-2 border-[#181816]/15 pt-6">
                  <p className="font-serif italic text-3xl text-[#B85D3B]">{m.num}</p>
                  <h3 className="mt-3 font-serif text-xl">{m.title}</h3>
                  <p className="mt-3 text-sm font-light text-[#57534E] leading-relaxed">{m.body}</p>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        <FadeUp>
          <div className="mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <h3 className="font-serif text-2xl sm:text-4xl">Ready when you are.</h3>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-3 bg-[#181816] text-[#F8F6F0] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-[#B85D3B] transition-colors duration-300"
            >
              Begin with an Inquiry <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </main>
  );
}
